import {useEffect, useRef} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useImmer} from 'use-immer';

import {matrixSelector, setQuantity} from '../../slices/matrixSlice';
import {appSelector} from '../../slices/appSlice';

const VISIBLE_COUNT = 3;

const usePriceSelection = () => {
  const {
    isFetching,
    customQuantities,
    prices,
    config,
    productId,
    defaultConfig,
    optionsOverwrite,
    priceListStyle,
    customShape: {dpoData},
    config: {is_custom_shape},
    AveryCustom: {client, customVat},
  } = useSelector(matrixSelector);

  const {priceDisplay, serviceLinks} = useSelector(appSelector);
  const contactLink = serviceLinks.find(({icon}) => icon === 'phone');
  const priceSequence = priceDisplay.split('_');
  const dispatch = useDispatch();
  const customQuantityField = useRef();
  const containerRef = useRef();

  const [state, updateState] = useImmer({
    showAll: false,
    loadingIndex: null,
    quantityDisabled: true,
    showRangeOverflowText: false,
  });

  // Visible range is derived from the selected index.
  // Always show from the bottom up to selected + 1 tier above.
  const values = prices.values;
  const selectedIndex = values.findIndex((v) => v.is_selected);
  const visibleStart = 0;
  const visibleEnd = Math.min(values.length, Math.max(VISIBLE_COUNT, selectedIndex + 2));

  const selectQuantity = ({parentId, id, is_selected}) => {
    if (!is_selected && !isFetching) {
      updateState((draft) => {
        draft.loadingIndex = `${parentId}_${id}`;
        draft.showAll = false;
      });

      dispatch(
        setQuantity({
          variables: {
            productId,
            client,
            customVat,
            defaultConfig,
            optionsOverwrite: {...optionsOverwrite, [parentId]: id},
            customQuantities,
          },
        })
      );
    }
  };

  const submitCustomQuantity = (customQuantity) => {
    const existing = prices.values.find(
      (price) => price.quantity === parseInt(customQuantity)
    );
    if (existing) {
      if (!existing.is_selected) {
        selectQuantity({parentId: prices.id, ...existing});
      }
    } else {
      dispatch(
        setQuantity({
          variables: {
            productId,
            client,
            customVat,
            optionsOverwrite: {
              ...optionsOverwrite,
              quantity: `custom_qty_${customQuantity}`,
            },
            customQuantities: [...customQuantities, customQuantity],
          },
        })
      );
    }
  };

  const shouldShowRangeOverflowText = (rangeOverflow) => {
    const textDifferent =
      Drupal.t('The quantity must be less than @max pieces.', {'@max': 1}) !==
      'The quantity must be less than 1 pieces.';
    return textDifferent && rangeOverflow;
  };

  const onCustomQuantityChange = () => {
    if (!customQuantityField.current) return;
    const validity = customQuantityField.current.checkValidity();
    const {rangeOverflow} = customQuantityField.current.validity;

    updateState((draft) => {
      draft.quantityDisabled = !validity;
      draft.showRangeOverflowText = shouldShowRangeOverflowText(rangeOverflow);
    });

    if (!shouldShowRangeOverflowText(rangeOverflow)) {
      customQuantityField.current.reportValidity();
    }
  };

  const onCustomQuantityKeyDown = (e) => {
    if (!customQuantityField.current) return;
    if ((e.code === 'Enter' || e.code === 'NumpadEnter') && customQuantityField.current.checkValidity()) {
      submitCustomQuantity(e.target.value);
    }
  };

  const onCustomQuantityBlur = () => {
    if (!customQuantityField.current) return;
    const value = customQuantityField.current.value;
    if (value && customQuantityField.current.checkValidity()) {
      submitCustomQuantity(value);
    }
  };

  const toggleExpanded = () => {
    updateState((draft) => {
      draft.showAll = !state.showAll;
    });
  };

  useEffect(() => {
    if (isFetching) {
      if (state.loadingIndex === null && customQuantityField.current) {
        customQuantityField.current.value = '';
        customQuantityField.current.blur();
      }
    } else {
      const wasLoading = state.loadingIndex !== null;
      updateState((draft) => {
        draft.loadingIndex = null;
      });

      if (wasLoading && containerRef.current) {
        requestAnimationFrame(() => {
          containerRef.current?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
        });
      }
    }
  }, [isFetching]);

  return {
    isFetching,
    prices,
    config,
    priceSequence,
    priceListStyle,
    contactLink,
    dpoData,
    is_custom_shape,

    visibleStart,
    visibleEnd,
    showAll: state.showAll,
    loadingIndex: state.loadingIndex,
    quantityDisabled: state.quantityDisabled,
    showRangeOverflowText: state.showRangeOverflowText,

    containerRef,
    customQuantityField,

    selectQuantity,
    submitCustomQuantity,
    toggleExpanded,
    onCustomQuantityChange,
    onCustomQuantityKeyDown,
    onCustomQuantityBlur,
  };
};

export default usePriceSelection;
